import { HttpClient, HttpParams } from '@angular/common/http';
import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import { extractAllParamsFromRouter, generateSensorKey } from '@twaice-fe/frontend/shared/utilities';
import { INTERNAL_UI_CONFIG_OVERRIDE, SKIP_REQUESTED_SYSTEMS_INTERCEPTOR_HEADER } from '@twaice-fe/shared/constants';
import {
  ComputationType,
  CustomerType,
  DataNotFoundError,
  HierarchyLevel,
  HierarchyLevelV2,
  SensorSpecDataResponse,
  SensorSpecResponse,
  SensorV2,
  SystemDetails,
  TWSingleResponse,
  UiConfig,
} from '@twaice-fe/shared/models';
import { keysToCamel } from '@twaice-fe/shared/utilities';
import { BehaviorSubject, Observable, Subscription, combineLatest, firstValueFrom, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, skipWhile, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
const { systemSelectors, authSelectors, configsSelectors } = selectors;
const { systemActions } = actions;

@Injectable({
  providedIn: 'root',
})
export class SystemsService implements OnDestroy {
  userSubscription: Subscription;

  private systemList$: Observable<SystemDetails[]> = new BehaviorSubject<SystemDetails[]>([]);
  private systemUiConfig: BehaviorSubject<UiConfig> = new BehaviorSubject<UiConfig>(null);
  private uiConfig: UiConfig;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private router: Router,
    protected store: Store,
    private destroy$: DestroyRef
  ) {
    this.systemList$ = this.store.select(systemSelectors.getSystemDetailsList);
    this.updateCurrentSystemUiConfiguration();

    const routerEndEvents = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroy$)
    );

    combineLatest([this.systemList$, this.store.select(systemSelectors.getSelected), routerEndEvents])
      .pipe(
        skipWhile(() => !this.authService.isLoggedIn),
        first(([systemList]) => !!(systemList && systemList.length)),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe(([systemList]) => {
        const params = extractAllParamsFromRouter(this.router);
        const systemBk = params['systemBk'];
        // if the route still contains a legacy system ID query param we use it and set that system as active
        const legacySystemId = new URLSearchParams(window.location.search).get('systemID');

        if (!systemBk) return this.setCurrentSystem(systemList.find((s) => s.id === legacySystemId) || systemList[0]);

        const systemMatch = systemList.find((system) => system.systemBk === systemBk);

        if (systemMatch) {
          this.setCurrentSystem(systemMatch);
          return;
        }

        alert('ERROR: Invalid URL parameters! You tried to access a system that your user credentials do not have access to.');

        return this.store.dispatch(systemActions.selectSystem({ systemId: systemList[0].id }));
      });

    this.authService.waitForAuthComplete().then(() => {
      combineLatest([this.authService.getUserObservable(), this.store.select(configsSelectors.getConfigsState)])
        .pipe(
          distinctUntilChanged((prev, curr) => {
            const { storageNameMappings: prevStorageNameMappings, ...prevConfigExcludingMappings } = prev[1];
            const { storageNameMappings: currStorageNameMappings, ...currConfigExcludingMappings } = curr[1];

            return (
              JSON.stringify({ user: prev[0], config: prevConfigExcludingMappings }) ===
              JSON.stringify({ user: curr[0], config: currConfigExcludingMappings })
            );
          }),
          takeUntilDestroyed(this.destroy$)
        )
        .subscribe(([user, config]) => {
          if (!user || !config) return;
          if (config.customerType === undefined || config.customerType === CustomerType.MODELS) return;

          this.store.dispatch(systemActions.fetchSystemDetails());
        });
    });
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  async handleLogout() {
    this.resetConfig();
    await this.authService.logout();
  }

  getCurrentSystem(): Observable<SystemDetails> {
    return this.store.select(systemSelectors.getSelected).pipe(filter((system) => !!system));
  }

  getCurrentTopLevelContainerId(): Observable<string> {
    return this.store.select(systemSelectors.getSelected).pipe(map((system) => system.systemBk));
  }

  async setCurrentSystem(system: SystemDetails) {
    if (!system) return;

    const current = await firstValueFrom(this.store.select(systemSelectors.getSelected));

    // only set the next value if the system actually changes(or you can trigger unnecessary reloading)
    if (!current || current.id !== system.id) {
      this.store.dispatch(systemActions.selectSystem({ systemId: system.id }));
    }

    // not to reset the UI config if UIConfig is already set
    if (!this.uiConfig) {
      this.updateCurrentSystemUiConfiguration();
    }
  }

  getCurrentSystemIdObservable(): Observable<string> {
    return this.store.select(systemSelectors.getSelectedId).pipe(filter((id) => id !== null));
  }

  /*
   * A function that returns a promise of a string for system ID. We want it to be a promise rather then observable, since we want it to
   * provide onetime value to the current user, but we want to wait for the value to actually be returned by the http request
   * */
  getCurrentSystemId(): Promise<string> {
    return firstValueFrom(this.getCurrentSystemIdObservable());
  }

  getCurrentSystemUiConfiguration(): Promise<UiConfig> {
    if (!this.systemUiConfig.getValue()) {
      return this.systemUiConfig
        .pipe(first((system) => system !== null))
        .toPromise()
        .catch((error) => {
          console.error('Router processor', error);
          // eslint-disable-next-line twaice/no-router-navigate-outside-effects
          this.router.navigate(['/missing-configuration']);
        })
        .then((config: UiConfig) => config);
    } else {
      return Promise.resolve(this.systemUiConfig.getValue());
    }
  }

  getSystemUiConfiguration(): Observable<UiConfig> {
    return this.systemUiConfig.asObservable();
  }

  updateCurrentSystemUiConfiguration(): void {
    const uiConfigOverride = JSON.parse(localStorage.getItem(INTERNAL_UI_CONFIG_OVERRIDE));
    if (uiConfigOverride) {
      this.uiConfig = uiConfigOverride.uiConfig;
      this.systemUiConfig.next(uiConfigOverride.uiConfig);
      return;
    }

    this.store
      .select(authSelectors.getUser)
      .pipe(
        filter((user) => !!user),
        switchMap((user) =>
          this.http.get<UiConfig>(
            `ui-config?customerBK=${user['attributes']['custom:customer_bk'] || user['attributes']['custom:customer_id']}`
          )
        )
      )
      .subscribe({
        next: (config) => {
          this.uiConfig = config;
          this.systemUiConfig.next(config);
        },
        error: (error) => this.systemUiConfig.error(error),
      });
  }

  resetConfig() {
    this.systemUiConfig.next(null);
    this.uiConfig = null;
  }

  mapToSensor(sensorSpecResponse: SensorSpecResponse): SensorV2 {
    return <SensorV2>{
      sensorBk: sensorSpecResponse.sensor_bk,
      levelBk: sensorSpecResponse.level_bk as HierarchyLevel | HierarchyLevelV2,
      sensorType: sensorSpecResponse.sensor_type,
      sensorSubtype: sensorSpecResponse.sensor_subtype,
      aggregationType: sensorSpecResponse.aggregation_type,
      aggregationLevel: sensorSpecResponse.aggregation_level,
      computationType: sensorSpecResponse.computation_type as ComputationType,
      valueType: sensorSpecResponse.value_type,
      unit: sensorSpecResponse.unit,
      name: sensorSpecResponse.name,
      standardSensorBk: sensorSpecResponse.standard_sensor_bk,
      key: generateSensorKey(
        sensorSpecResponse.name,
        sensorSpecResponse.level_bk as HierarchyLevel | HierarchyLevelV2,
        sensorSpecResponse.computation_type
      ),
    };
  }

  getComponentSensorSpecs({
    customerBk,
    systemBk,
    componentBk,
    sensorType,
    sensorSubType,
    aggregationType,
    aggregationLevel,
    valueType,
  }: {
    customerBk: string;
    systemBk: string;
    componentBk: string;
    sensorType?: string;
    sensorSubType?: string;
    aggregationType?: string;
    aggregationLevel?: string;
    valueType?: string;
  }): Observable<SensorV2[]> {
    const url = `data-explorer/customers/${customerBk}/systems/${systemBk}/component/${componentBk}/sensor_specifications`;
    let params = new HttpParams();

    if (sensorType) params = params.set('sensor_type_bk', sensorType);
    if (sensorSubType) params = params.set('sensor_subtype', sensorSubType);
    if (aggregationType) params = params.set('aggregation_type', aggregationType);
    if (aggregationLevel) params = params.set('aggregation_level', aggregationLevel);
    if (valueType) params = params.set('value_type', valueType);

    params = params.set('is_hierarchy_v2', 'true');

    return this.http.get<SensorSpecDataResponse>(url, { params }).pipe(
      map((response: SensorSpecDataResponse) => response.data.items),
      map((sensors: SensorSpecResponse[]) => sensors.map((sensor) => this.mapToSensor(sensor)))
    );
  }

  fetchSystemDetails(fetchAll = false): Observable<SystemDetails[]> {
    const options = fetchAll ? { headers: { [SKIP_REQUESTED_SYSTEMS_INTERCEPTOR_HEADER]: 'true' } } : {};
    return this.http.get<TWSingleResponse<SystemDetails[]>>('analytics/systems', options).pipe(
      map(({ data }) => {
        if (!(data as unknown)) {
          throw new DataNotFoundError(`No system details data found for the given customer.`);
        }
        return keysToCamel(
          data.map((system) => ({ ...system, id: system['system_tid'] || `${system['customer_bk']}/${system['system_bk']}` }))
        ).map((system: SystemDetails) => ({
          ...system,
          // the API may not send this field yet; treat a missing/unset flag as "onboarded" for backwards compatibility
          isSystemOnboarded: system.isSystemOnboarded ?? true,
        }));
      }),
      catchError((error: Error) => {
        // eslint-disable-next-line twaice/no-router-navigate-outside-effects
        this.router.navigate(['/']);
        const snackBarRef = this.snackBar.open(
          'Error occurred while trying to get a system list. Logging out might resolve it: ',
          'Log out',
          {
            verticalPosition: 'top',
          }
        );

        snackBarRef.onAction().subscribe(() => this.handleLogout());
        return throwError(() => error);
      })
    );
  }
}
